import type { DayHours } from '@/bindings/DayHours'
import type { HoursMode } from '@/bindings/HoursMode'
import type { OpeningHours } from '@/bindings/OpeningHours'
import type { Weekday } from '@/bindings/Weekday'
import type { Translate } from '@/lib/i18n/translate'

export const WEEK: readonly Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
export const WEEKDAYS: readonly Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri']
export const WEEKEND: readonly Weekday[] = ['sat', 'sun']
export const HOURS_MODES: readonly HoursMode[] = ['same', 'weekday_weekend', 'per_day']

export const DEFAULT_OPEN = '08:00'
export const DEFAULT_CLOSE = '22:00'

/**
 * Every day with both times kept, so closing and reopening a day keeps what was typed. With no
 * stored hours every day starts open at the default range — a starting point, saved only when
 * the manager ticks "show".
 */
export type DaysDraft = Record<Weekday, { closed: boolean; open: string; close: string }>

export function daysDraftFrom(hours: OpeningHours | null | undefined): DaysDraft {
  const draft = {} as DaysDraft
  for (const day of WEEK) {
    const stored = hours?.days[day]
    draft[day] = {
      closed: stored?.closed ?? hours != null,
      open: stored?.open ?? DEFAULT_OPEN,
      close: stored?.close ?? DEFAULT_CLOSE,
    }
  }
  return draft
}

/** The day whose times a group's inputs show: its first open day (any day when all are closed). */
export function groupAnchor(group: readonly Weekday[], days: DaysDraft): Weekday {
  return group.find((day) => !days[day].closed) ?? group[0]!
}

/**
 * The request shape: every open day carries its group's shown range, closed days no times — what
 * is saved is what the editor showed, whatever the stored days said.
 */
export function toOpeningHours(mode: HoursMode, draft: DaysDraft): OpeningHours {
  const days: OpeningHours['days'] = {}
  for (const group of dayGroups(mode)) {
    const { open, close } = draft[groupAnchor(group, draft)]
    for (const day of group) days[day] = draft[day].closed ? { closed: true } : { closed: false, open, close }
  }
  return { mode, days }
}

/** "Monday–Friday" or "Saturday" — how the editor names a group's time range. */
export function groupLabel(group: readonly Weekday[], t: Translate): string {
  const first = t(`day.${group[0]!}`)
  return group.length === 1 ? first : `${first}–${t(`day.${group[group.length - 1]!}`)}`
}

/** The runs of day-rows that share one time range in the editor, per mode. */
export function dayGroups(mode: HoursMode): readonly (readonly Weekday[])[] {
  if (mode === 'same') return [WEEK]
  if (mode === 'weekday_weekend') return [WEEKDAYS, WEEKEND]
  return WEEK.map((day) => [day])
}

function normalize(hours: DayHours | undefined): DayHours {
  if (!hours || hours.closed || !hours.open || !hours.close) return { closed: true }
  return { closed: false, open: hours.open, close: hours.close }
}

function same(a: DayHours, b: DayHours): boolean {
  if (a.closed || b.closed) return a.closed === b.closed
  return a.open === b.open && a.close === b.close
}

/**
 * Display lines, consecutive days with the same hours collapsed:
 * ["Tuesday–Sunday: 16:00–21:00", "Monday: closed"]. Day names come from the UI language.
 */
export function formatOpeningHours(hours: OpeningHours | null | undefined, t: Translate): string[] {
  if (!hours) return []
  const runs: { days: Weekday[]; value: DayHours }[] = []
  for (const day of WEEK) {
    const value = normalize(hours.days[day])
    const last = runs[runs.length - 1]
    if (last && same(last.value, value)) last.days.push(day)
    else runs.push({ days: [day], value })
  }
  return runs.map(({ days, value }) => {
    const label = groupLabel(days, t)
    return value.closed ? `${label}: ${t('openingHours.closed')}` : `${label}: ${value.open}–${value.close}`
  })
}

